import { useMutation } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { useAnalytics } from "../../../contexts/analytics/hooks";
import { useHospitalData } from "../../../contexts/HospitalDataProvider";
import { useRequestsContext } from "../../../contexts/RequestsContext";
import { useSnackbar } from "../../../contexts/SnackbarContext";
import { backendService } from "../../../services/backend.service";
import { showAgencyPostingResult } from "../../../utils/agencyWarnings";
import type { AgencySource, NurseRole } from "../../../utils/models";

interface PostRequestToAgencyParams {
	requestId: string;
	/** The single chosen agency, sent as the backend's 1-element `agency_source` array. */
	agencySource: AgencySource[];
	/** The per-(job, agency) sub-role pick; omitted when the catalog has no sub-roles. */
	subRole?: NurseRole;
	postWithDelay?: boolean;
	agencyPostDelayHours?: number;
}

/**
 * Posts an existing open-shift request to an agency (`POST /staffing/post-request-to-agency/{id}`).
 * On success refreshes the requests list and surfaces non-fatal `agency_posting_failures` as warnings;
 * on error shows the translated `UIVisibleError`.
 */
export const usePostRequestToAgency = () => {
	const { chosenDepartment, showErrorSnackbar } = useHospitalData();
	const { refreshRequests } = useRequestsContext();
	const { showSnackbar } = useSnackbar();
	const { track, trackError } = useAnalytics();
	const { t } = useTranslation();

	return useMutation({
		mutationFn: ({ requestId, agencySource, subRole, postWithDelay, agencyPostDelayHours }: PostRequestToAgencyParams) =>
			backendService.postRequestToAgency(requestId, chosenDepartment.department_id, {
				agency_source: agencySource,
				sub_role: subRole,
				post_with_delay: postWithDelay,
				agency_post_delay_hours: agencyPostDelayHours,
			}),
		onMutate: ({ requestId, agencySource }: PostRequestToAgencyParams) => {
			track("post_request_to_agency_start", {
				request_id: requestId,
				department_id: chosenDepartment.department_id,
				agency_source: agencySource[0],
			});
		},
		onSuccess: async (response, { requestId, agencySource }) => {
			track("post_request_to_agency_success", {
				request_id: requestId,
				department_id: chosenDepartment.department_id,
				agency_source: agencySource[0],
			});
			await refreshRequests();
			// A scheduled post hasn't reached the agency yet, so don't claim it has.
			const message = response.data.scheduled ? t("agency.posting.scheduled") : t("posted.to.agency.successfully");
			showAgencyPostingResult(showSnackbar, message, t, response.data.agency_posting_failures);
		},
		onError: (error: Error, { requestId, agencySource }) => {
			trackError("post_request_to_agency_error", {
				request_id: requestId,
				department_id: chosenDepartment.department_id,
				agency_source: agencySource[0],
				error_message: error.message,
			});
			showErrorSnackbar(t("error.posting.to.agency.failed"), { action: "post request to agency failed" }, error);
		},
	});
};
